import type { ReactNode } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { Kbd, SheetBody, SheetContent, WeightGlyph } from '@/components/ui';
import { WEIGHT_OPTIONS } from '@/logic/weights';

const DOCS_URL = 'https://github.com/silentpills/resolve/blob/main/docs';

const GUIDES = [
  { label: 'Getting started', path: 'user-guide/getting-started.md' },
  { label: 'Weights and statuses', path: 'user-guide/weights.md' },
  { label: 'Blunder detection', path: 'user-guide/blunder-detection.md' },
  { label: 'File formats', path: 'user-guide/file-formats.md' },
  { label: 'Transformation theory', path: 'concepts/transformation.md' },
] as const;

const STEPS = [
  {
    label: 'Import',
    text: 'Load Record (plat or deed) and Field (measured) coordinates.',
  },
  {
    label: 'Adjust',
    text: 'Review matches and weights, solve, then check for blunders. Confirm field evidence before applying a rejection.',
  },
  {
    label: 'Export',
    text: 'Transform Record points with the current solution and download coordinates, the log, or a printed report.',
  },
] as const;

const TERMS = [
  ['RMS', 'Scatter of the active residuals, in project units.'],
  [
    'σ₀',
    'Standard error of unit weight. Drives the statistical tests and uncertainty estimates.',
  ],
  [
    'Radial σ',
    'Predicted position uncertainty of a transformed point (about 63%; ×1.73 for 95%).',
  ],
  [
    'Verdict',
    'Both detection methods flagging the same pairs is strong evidence. Disagreement or an inconclusive result means inspect the field evidence.',
  ],
] as const;

const SHORTCUTS: ReadonlyArray<[ReactNode, string]> = [
  [
    <>
      <Kbd>←</Kbd>
      <Kbd>→</Kbd>
      <Kbd>Home</Kbd>
      <Kbd>End</Kbd>
    </>,
    'Move between workflow steps',
  ],
  [
    <>
      <Kbd>U</Kbd>
      <Kbd>F</Kbd>
      <Kbd>X</Kbd>
      <Kbd>I</Kbd>
    </>,
    'Set the selected map pair to Unity, Free, Fixed, or Ignore',
  ],
  [<Kbd key="esc">Esc</Kbd>, 'Close a dialog or cancel a long calculation'],
];

const Section = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <section className="space-y-3">
    <h3 className="text-[11px] font-medium uppercase tracking-wide text-fg-muted">
      {title}
    </h3>
    {children}
  </section>
);

/** Help sheet content: a concise, scannable reference with links to the guides. */
export const HelpSheetContent = () => (
  <SheetContent
    title="Help"
    description="Quick reference. The guides cover each step in depth."
    className="print:hidden"
  >
    <SheetBody className="space-y-7 pb-8">
      <Section title="Workflow">
        <ol className="space-y-3">
          {STEPS.map((step, index) => (
            <li key={step.label} className="flex gap-3">
              <span
                aria-hidden="true"
                className="mt-px flex size-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-accent ring-1 ring-inset ring-accent/40"
              >
                {index + 1}
              </span>
              <p className="text-fg-muted">
                <span className="font-medium text-fg">{step.label}.</span>{' '}
                {step.text}
              </p>
            </li>
          ))}
        </ol>
      </Section>

      <Section title="Weights">
        <dl className="divide-y divide-border overflow-hidden rounded-lg border border-border">
          {WEIGHT_OPTIONS.map((option) => (
            <div
              key={option.status}
              className="flex items-start gap-3 bg-surface px-3 py-2.5"
            >
              <WeightGlyph status={option.status} className="mt-1" />
              <dt className="w-14 shrink-0 font-medium text-fg">
                {option.label}
              </dt>
              <dd className="min-w-0 flex-1 text-fg-muted">
                {option.description}
              </dd>
              <dd className="shrink-0 font-mono text-xs leading-5 tabular-nums text-fg-muted">
                {option.value}
              </dd>
            </div>
          ))}
        </dl>
        <p className="text-xs text-fg-muted">
          One Fixed pair anchors the fit; two Fixed pairs constrain it.
        </p>
      </Section>

      <Section title="Reading results">
        <dl className="space-y-2.5">
          {TERMS.map(([term, text]) => (
            <div key={term} className="grid grid-cols-[5rem_1fr] gap-3">
              <dt className="font-medium text-fg">{term}</dt>
              <dd className="text-fg-muted">{text}</dd>
            </div>
          ))}
        </dl>
      </Section>

      <Section title="Keyboard">
        <ul className="space-y-2.5">
          {SHORTCUTS.map(([keys, text]) => (
            <li key={text} className="flex items-start gap-3">
              <span className="flex w-32 shrink-0 flex-wrap gap-1">{keys}</span>
              <span className="text-fg-muted">{text}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Saving">
        <p className="text-fg-muted">
          Inputs and current results autosave in this browser; detailed
          detection results are left out.{' '}
          <strong className="font-medium text-fg">
            Project → Save project
          </strong>{' '}
          downloads a complete{' '}
          <span className="font-mono text-xs">.resolve</span> file you can open
          later.
        </p>
      </Section>

      <Section title="Guides">
        <ul className="-mx-2">
          {GUIDES.map((guide) => (
            <li key={guide.path}>
              <a
                href={`${DOCS_URL}/${guide.path}`}
                target="_blank"
                rel="noreferrer"
                className="group flex items-center justify-between rounded-md px-2 py-1.5 text-accent transition-colors hover:bg-surface-muted"
              >
                {guide.label}
                <ArrowUpRight
                  aria-hidden="true"
                  className="size-4 text-fg-muted transition-colors group-hover:text-accent"
                />
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
            </li>
          ))}
        </ul>
      </Section>
    </SheetBody>
  </SheetContent>
);
